import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Trash2 } from 'lucide-react';
import { useNotifications, type NotificationItem } from '../../contexts/NotificationsContext';
import {
  DATE_GROUP_LABEL,
  getDateGroup,
  getNotificationTypeStyle,
  relativeTime,
} from '../../lib/notification-style';

/**
 * Cloche notifications dans le header — US-NOTIF-02 + US-NOTIF-06.
 *
 *  - Badge rouge avec compteur de non-lues (cap "99+" si > 99).
 *  - Dropdown : 5 dernières notifs groupées par date.
 *  - Clic sur une notif → markRead + navigation vers `action_url`.
 *  - Actions globales : "Tout marquer comme lu" + "Voir tout" → /notifications.
 *  - Indicateur visuel particulier pour les `urgent` non lues.
 */

const PREVIEW_LIMIT = 5;

export function NotificationsBell() {
  const navigate = useNavigate();
  const {
    recent,
    unreadCount,
    connectionState,
    markRead,
    markAllRead,
    dismiss,
    browserPermission,
    requestBrowserPermission,
  } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function handleClickNotification(n: NotificationItem) {
    if (!n.is_read) markRead(n.id);
    setOpen(false);
    if (n.action_url) {
      navigate(n.action_url);
    }
  }

  const preview = recent.slice(0, PREVIEW_LIMIT);
  const groups = groupByDate(preview);
  const showBadge = unreadCount > 0;
  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="relative p-2 rounded-lg hover:bg-gray-100"
        aria-label="Notifications"
        title={
          connectionState === 'open'
            ? `${unreadCount} notification(s) non lue(s)`
            : 'Reconnexion en cours…'
        }
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {showBadge && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[1rem] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
            {badgeLabel}
          </span>
        )}
        {connectionState !== 'open' && (
          <span
            className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-orange-400"
            title="Connexion temps réel inactive"
          />
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] rounded-xl bg-white border border-gray-200 shadow-xl overflow-hidden">
          {/* Header dropdown */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center space-x-1">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => markAllRead()}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium px-2 py-1 rounded hover:bg-primary-50 inline-flex items-center"
                  title="Tout marquer comme lu"
                >
                  <CheckCheck className="w-3.5 h-3.5 mr-1" />
                  Tout lu
                </button>
              )}
            </div>
          </div>

          {/* Bandeau permission Web Notifications */}
          {browserPermission === 'default' && (
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-center justify-between">
              <span>Activer les notifications navigateur ?</span>
              <div className="space-x-2">
                <button
                  onClick={() => requestBrowserPermission()}
                  className="font-medium text-primary-600 hover:text-primary-700"
                >
                  Activer
                </button>
              </div>
            </div>
          )}

          {/* Liste */}
          <div className="max-h-96 overflow-y-auto">
            {preview.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-gray-500">
                Aucune notification pour le moment.
              </div>
            ) : (
              Object.entries(groups).map(([key, items]) =>
                items.length === 0 ? null : (
                  <Fragment key={key}>
                    <div className="px-4 py-1.5 bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      {DATE_GROUP_LABEL[key as keyof typeof DATE_GROUP_LABEL]}
                    </div>
                    {items.map((n) => (
                      <NotificationRow
                        key={n.id}
                        notif={n}
                        onClick={() => handleClickNotification(n)}
                        onMarkRead={() => markRead(n.id)}
                        onDismiss={() => dismiss(n.id)}
                      />
                    ))}
                  </Fragment>
                ),
              )
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 px-4 py-2 bg-gray-50">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="block text-center text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              Voir toutes les notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationRow({
  notif,
  onClick,
  onMarkRead,
  onDismiss,
}: {
  notif: NotificationItem;
  onClick(): void;
  onMarkRead(): void;
  onDismiss(): void;
}) {
  const style = getNotificationTypeStyle(notif.type);
  const Icon = style.icon;
  const isUrgentUnread = !notif.is_read && notif.priority === 'urgent';
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={`group flex items-start gap-3 px-4 py-3 cursor-pointer border-b border-gray-50 last:border-0 transition-colors ${
        notif.is_read ? 'bg-white hover:bg-gray-50' : 'bg-blue-50/50 hover:bg-blue-50'
      } ${isUrgentUnread ? 'border-l-4 border-l-red-500' : ''}`}
    >
      <div className="flex-shrink-0 mt-0.5">
        <Icon className={`w-5 h-5 ${style.className}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm leading-tight ${notif.is_read ? 'text-gray-700' : 'font-semibold text-gray-900'}`}
        >
          {notif.title}
        </p>
        <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{notif.message}</p>
        <p className="text-[11px] text-gray-400 mt-1">{relativeTime(notif.created_at)}</p>
      </div>
      <div className="flex-shrink-0 flex flex-col items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {!notif.is_read && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onMarkRead();
            }}
            className="p-1 rounded hover:bg-gray-200 text-gray-500"
            title="Marquer comme lue"
            aria-label="Marquer comme lue"
          >
            <Check className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="p-1 rounded hover:bg-red-50 text-gray-500 hover:text-red-600"
          title="Supprimer"
          aria-label="Supprimer"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function groupByDate(
  items: NotificationItem[],
): Record<ReturnType<typeof getDateGroup>, NotificationItem[]> {
  const result: Record<ReturnType<typeof getDateGroup>, NotificationItem[]> = {
    today: [],
    yesterday: [],
    thisWeek: [],
    older: [],
  };
  for (const n of items) {
    const group = getDateGroup(new Date(n.created_at));
    result[group].push(n);
  }
  return result;
}
